'use client';

import { useState } from 'react';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { ProductCard } from './product-card';
import { Button } from '@sapling/ui/components/ui/button';
import type { PublicProduct } from '@sapling/contracts/products';

type ProductGridProps = {
  products: PublicProduct[];
};

export function ProductGrid({ products }: ProductGridProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Get unique categories
  const categories = [
    'all',
    ...Array.from(new Set(products.map((product) => product.category))),
  ];

  // Filter products by category
  const filteredProducts =
    selectedCategory === 'all'
      ? products
      : products.filter((product) => product.category === selectedCategory);

  const categoryDisplayNames: Record<string, string> = {
    all: 'Alla produkter',
    events: 'Evenemang',
    publications: 'Publikationer',
    education: 'Utbildning',
  };

  return (
    <>
      <div className="space-y-8">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {categoryDisplayNames[category] || category}
            </Button>
          ))}
        </div>

        {/* Products Count */}
        <div className="text-muted-foreground text-sm">
          {filteredProducts.length}{' '}
          {filteredProducts.length === 1 ? 'produkt' : 'produkter'}
          {selectedCategory !== 'all' &&
            ` i kategorin "${categoryDisplayNames[selectedCategory]}"`}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProducts.map((product) => (
            <ProductCard key={product.sku} product={product} />
          ))}
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="py-12 text-center">
            <ShoppingBagIcon className="text-muted-foreground/50 mx-auto h-12 w-12" />
            <h3 className="mt-4 text-lg font-medium">
              Inga produkter hittades
            </h3>
            <p className="text-muted-foreground">
              Prova att välja en annan kategori eller kom tillbaka senare.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
